<script setup lang="ts">
import { type PropType, computed } from 'vue'
import { createReusableTemplate } from '@vueuse/core'
import { toast } from 'vue-sonner'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import BaseTag from '@/components/ui/tag/BaseTag.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import { useBreakpoints, getCouponEventExtra } from '@/lib/utils'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import {
	type DiscountTypeValue,
	type ApiOrderTypeValue,
	ApiOrderType,
	DiscountType,
} from '@/types/Coupon'

const { isTabletPortrait } = useBreakpoints()
const [DefineCardContent, ReuseCardContent] = createReusableTemplate()

defineOptions({
	inheritAttrs: false,
})

const props = defineProps({
	orderType: {
		type: String as PropType<ApiOrderTypeValue>,
		default: ApiOrderType.Shipping,
	},
	discountType: {
		type: String as PropType<DiscountTypeValue>,
		default: DiscountType.FIXED,
	},
	title: {
		type: String,
		required: true,
	},
	couponCode: {
		type: String,
		required: true,
	},
	couponAmount: {
		type: [Number, null] as PropType<number | null>,
		required: true,
	},
	percentageDiscount: {
		type: [Number, null] as PropType<number | null>,
		required: true,
	},
	startDate: {
		type: [String, null] as PropType<string | null>,
		required: true,
	},
	expiryDate: {
		type: String,
		required: true,
	},
	isExpireSoon: {
		type: Boolean,
		required: true,
	},
	terms: {
		type: String,
		required: true,
	},
	isDisabled: {
		type: Boolean,
		default: false,
	},
	isInModal: {
		type: Boolean,
		default: false,
	},
	text: {
		type: Object,
		default: null,
	},
	currentPageName: {
		type: String,
		default: 'Coupon Detail Popup Page',
	},
})

const emit = defineEmits(['showModal'])

const displayedAmount = computed(() => {
	if (props.discountType === DiscountType.FIXED && props.couponAmount) {
		const formattedResult = `${props.couponAmount.toLocaleString('en', {
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
		})}`

		return props.text.coupon_card_amount.replace('{amount}', formattedResult)
	}
	if (props.discountType === DiscountType.PERCENTAGE) {
		return `${props.percentageDiscount}%`
	}
	if (props.discountType === DiscountType.WAIVE_SERVICE_FEE) {
		return '0%'
	}

	return ''
})

const displayedAmountSuffix = computed(() => {
	if (props.discountType === DiscountType.PERCENTAGE) {
		return props.text.coupon_card_percentage_off
	}
	if (props.discountType === DiscountType.WAIVE_SERVICE_FEE) {
		return props.text.coupon_card_shipment_unit
	}

	return ''
})

const displayedAmountStyle = computed(() => {
	if (!isTabletPortrait.value) {
		return 'typography-h2-str'
	}

	const numberLength = displayedAmount.value.replace(/\D/g, '').length
	return numberLength < 5
		? 'typography-h1-str !text-[2.75rem] !leading-[3.5rem]'
		: 'typography-h2-str'
})

const displayedCouponCode = computed(() => {
	if (props.isInModal) {
		return props.couponCode
	}

	const MAX_CODE_LENGTH = 100
	return props.couponCode.length > MAX_CODE_LENGTH
		? `${props.couponCode.slice(0, MAX_CODE_LENGTH)}...`
		: props.couponCode
})

const displayedExpiryDate = computed(() => {
	return props.text.coupon_card_expiry_date_description.replace(
		'{date}',
		props.expiryDate,
	)
})

const displayedOrderType = computed(() => {
	if (props.orderType === ApiOrderType.ExpressCheckout) {
		return props.text.coupon_card_service_type_buyandshop_title
	}
	if (props.orderType === ApiOrderType.ProxyShopping) {
		return props.text.coupon_card_service_type_buyforyou_title
	}
	if (props.orderType === ApiOrderType.Shipping) {
		return props.text.coupon_card_service_type_buyandship_title
	}

	return ''
})

const handleCopyCouponCode = () => {
	toast.success(props.text.coupon_card_copy_success)
	navigator.clipboard.writeText(props.couponCode)

	const pageName =
		props.isInModal &&
		props.currentPageName === GaGeneralPageName.MercariCampaign
			? props.currentPageName
			: 'Coupon Detail Popup Page'

	const buttonName =
		props.currentPageName === GaGeneralPageName.MercariCampaign
			? 'Copy Coupon Code-Detail'
			: 'Copy Coupon Code'

	postButtonClickGAEvent(buttonName, pageName)
}

const handleDetailsClick = () => {
	emit('showModal')

	postButtonClickGAEvent('Show Coupon Detail')
}

const couponEventExtra = computed(() => {
	return getCouponEventExtra(props.orderType, props.couponCode)
})

const postButtonClickGAEvent = (
	buttonName: string,
	pageName: string = props.currentPageName,
) => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: pageName,
		button_name: buttonName,
		event_extra: couponEventExtra.value,
	})
}
</script>

<template>
	<DefineCardContent>
		<h3
			class="typography-s1-reg mb-4px whitespace-pre-line break-words text-n-950 mdp:mb-8px"
		>
			{{ title }}
		</h3>

		<div
			class="mb-8px flex flex-col items-start gap-12px mdp:mb-12px mdp:flex-row mdp:justify-between"
		>
			<div class="flex items-center gap-8px">
				<div class="typography-c1-reg text-n-400">
					{{ displayedExpiryDate }}
				</div>
				<BaseTag v-if="isExpireSoon" variant="error" size="small">
					{{ text.coupon_card_tag }}
				</BaseTag>
			</div>

			<BaseButton
				v-if="!isInModal"
				class="flex-none [&]:p-0"
				variant="tertiary"
				size="small"
				:is-disabled="isDisabled"
				@click="handleDetailsClick"
			>
				{{ text.coupon_card_details }}
			</BaseButton>
		</div>
	</DefineCardContent>

	<div
		class="group flex flex-grow flex-col mdp:flex-row [&.disabled_*]:text-n-400"
		:class="{ disabled: isDisabled }"
	>
		<div
			class="flex flex-col overflow-hidden rounded-t-[8px] border border-b-0 border-n-100 mdp:min-w-[240px] mdp:max-w-[240px] mdp:rounded-l-[8px] mdp:rounded-tr-none mdp:border-b mdp:border-r-0"
		>
			<div class="flex h-full flex-col">
				<div
					class="typography-c2-reg ml-auto w-min text-ellipsis whitespace-nowrap rounded-bl-[8px] bg-o-100-75% px-16px py-4px text-o-500 group-[.disabled]:bg-n-50 mdp:ml-0 mdp:mr-auto mdp:rounded-bl-none mdp:rounded-br-[8px]"
				>
					{{ displayedOrderType }}
				</div>
				<div
					class="flex items-center gap-8px px-16px text-left text-o-500 group-[.disabled_*]:text-n-300 mdp:my-auto mdp:flex-col mdp:justify-center mdp:gap-0 mdp:pb-32px mdp:pt-16px"
				>
					<div class="mdp:text-center" :class="displayedAmountStyle">
						{{ displayedAmount }}
					</div>
					<div
						class="typography-h2-str mdp:text-center"
						:class="[{ 'typography-h5-str ': isTabletPortrait }]"
					>
						{{ displayedAmountSuffix }}
					</div>
				</div>
			</div>

			<div v-if="!isTabletPortrait" class="mt-4px px-16px">
				<ReuseCardContent />
			</div>
		</div>

		<div
			class="-my-[0.5px] flex items-center justify-center mdp:-mx-[2px] mdp:-my-[3.5px] mdp:flex-col"
		>
			<BaseIcon
				name="coupon_divider_half_circle"
				class="rotate-180 mdp:rotate-[270deg]"
			/>
			<div class="h-full w-full px-8px mdp:py-8px">
				<div
					class="h-[1px] w-full bg-[linear-gradient(to_right,#e3e3e3_4px,transparent_4px)] bg-[length:8px_1px] bg-repeat-x mdp:h-full mdp:w-[1px] mdp:bg-[linear-gradient(to_bottom,#e3e3e3_4px,transparent_4px)] mdp:bg-[length:1px_8px] mdp:bg-repeat-y"
				></div>
			</div>
			<BaseIcon name="coupon_divider_half_circle" class="mdp:rotate-90" />
		</div>

		<div
			class="w-full overflow-hidden rounded-b-[8px] border border-t-0 border-n-100 p-16px pt-8px mdp:rounded-l-none mdp:rounded-tr-[8px] mdp:border-l-0 mdp:border-t mdp:pt-16px"
		>
			<div v-if="isTabletPortrait">
				<ReuseCardContent />
			</div>

			<div
				class="flex w-full items-center justify-between gap-24px rounded-[8px] bg-n-50 p-8px pl-16px"
			>
				<div class="typography-s1-str break-all text-n-950">
					{{ displayedCouponCode }}
				</div>

				<BaseButton
					size="small"
					@click="handleCopyCouponCode"
					class="flex-none group-[.disabled_*]:text-n-100"
					:is-disabled="isDisabled"
				>
					{{ text.coupon_card_copy }}
				</BaseButton>
			</div>
		</div>
	</div>
</template>
